<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, ref, type ComputedRef, type Ref } from "vue";
import type {
  EmailTemplate,
  EmailTemplateKey,
  EmailTemplatePreview,
  SaveEmailTemplateInput,
} from "../api/system-settings.js";
import { formatDateTime } from "../formatting/date-time.js";
import { useSystemSettingsStore } from "../stores/system-settings.js";
import { useUserSettingsStore } from "../stores/user-settings.js";

const settingsStore: ReturnType<typeof useSystemSettingsStore> = useSystemSettingsStore();
const userSettingsStore: ReturnType<typeof useUserSettingsStore> = useUserSettingsStore();
const templates: ComputedRef<readonly EmailTemplate[]> = computed(
  (): readonly EmailTemplate[] => settingsStore.emailTemplates?.templates ?? [],
);
const selectedKey: Ref<EmailTemplateKey | null> = ref(templates.value[0]?.key ?? null);
const enabled: Ref<boolean> = ref(true);
const subjectTemplate: Ref<string> = ref("");
const textBodyTemplate: Ref<string> = ref("");
const htmlBodyTemplate: Ref<string> = ref("");
const preview: Ref<EmailTemplatePreview | null> = ref(null);
const message: Ref<string | null> = ref(null);
const failed: Ref<boolean> = ref(false);
const saving: Ref<boolean> = ref(false);
const previewing: Ref<boolean> = ref(false);

const selectedTemplate: ComputedRef<EmailTemplate | null> = computed(
  (): EmailTemplate | null => templates.value.find(
    (template: EmailTemplate): boolean => template.key === selectedKey.value,
  ) ?? null,
);

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : "The email template request failed.";
}

function applyTemplate(template: EmailTemplate): void {
  selectedKey.value = template.key;
  enabled.value = template.enabled;
  subjectTemplate.value = template.subjectTemplate;
  textBodyTemplate.value = template.textBodyTemplate;
  htmlBodyTemplate.value = template.htmlBodyTemplate ?? "";
  preview.value = null;
  message.value = null;
  failed.value = false;
}

function input(): SaveEmailTemplateInput {
  return {
    enabled: enabled.value,
    subjectTemplate: subjectTemplate.value,
    textBodyTemplate: textBodyTemplate.value,
    htmlBodyTemplate: htmlBodyTemplate.value.trim() === "" ? null : htmlBodyTemplate.value,
  };
}

function placeholderToken(key: string): string {
  return `{{${key}}}`;
}

function formatLastSaved(value: string): string {
  return formatDateTime(value, userSettingsStore.settings.dateTimeFormat);
}

async function renderPreview(): Promise<void> {
  const key: EmailTemplateKey | null = selectedKey.value;
  if (key === null) {
    return;
  }
  previewing.value = true;
  message.value = null;
  failed.value = false;
  try {
    preview.value = await settingsStore.previewEmailTemplate(key, input());
  } catch (error: unknown) {
    failed.value = true;
    message.value = errorText(error);
  } finally {
    previewing.value = false;
  }
}

async function save(): Promise<void> {
  const key: EmailTemplateKey | null = selectedKey.value;
  if (key === null) {
    return;
  }
  saving.value = true;
  message.value = null;
  failed.value = false;
  try {
    const template: EmailTemplate = await settingsStore.updateEmailTemplate(key, input());
    applyTemplate(template);
    message.value = `Template saved as revision ${template.revision}.`;
  } catch (error: unknown) {
    failed.value = true;
    message.value = errorText(error);
  } finally {
    saving.value = false;
  }
}

if (selectedTemplate.value !== null) {
  applyTemplate(selectedTemplate.value);
}
</script>

<template>
  <section class="content-panel settings-card email-template-card">
    <div class="settings-card__heading">
      <div>
        <p class="eyebrow">Message content</p>
        <h2>Email templates</h2>
      </div>
      <span v-if="selectedTemplate" class="status-badge" :class="enabled ? 'status-badge--active' : 'status-badge--suspended'">
        {{ enabled ? "Enabled" : "Disabled" }}
      </span>
    </div>

    <div class="email-template-layout">
      <nav class="smtp-profile-list email-template-list" aria-label="Email templates">
        <button
          v-for="template in templates"
          :key="template.key"
          type="button"
          class="smtp-profile-list__item"
          :class="{ 'smtp-profile-list__item--selected': selectedKey === template.key }"
          @click="applyTemplate(template)"
        >
          <span><strong>{{ template.displayName }}</strong><small>{{ template.category.replace("_", " ") }} · revision {{ template.revision }}</small></span>
          <span class="status-dot" :class="template.enabled ? 'status-dot--active' : 'status-dot--disabled'"></span>
        </button>
      </nav>

      <form v-if="selectedTemplate" class="settings-form email-template-form" @submit.prevent="save">
        <div class="email-template-summary">
          <div><strong>{{ selectedTemplate.displayName }}</strong><p>{{ selectedTemplate.description }}</p></div>
          <label class="settings-checkbox"><input v-model="enabled" type="checkbox" /><span>Enable this template</span></label>
        </div>

        <label><span>Subject</span><input v-model="subjectTemplate" maxlength="320" required /></label>
        <label><span>Plain-text body</span><textarea v-model="textBodyTemplate" maxlength="100000" rows="11" required></textarea></label>
        <label><span>HTML body · optional</span><textarea v-model="htmlBodyTemplate" maxlength="200000" rows="14" spellcheck="false"></textarea></label>

        <div class="template-placeholders">
          <strong>Available placeholders</strong>
          <p>Required placeholders must appear in at least one field. Values are HTML-escaped in the HTML body.</p>
          <div>
            <code v-for="placeholder in selectedTemplate.placeholders" :key="placeholder.key" :title="`${placeholder.description} · Example: ${placeholder.example}`">
              {{ placeholderToken(placeholder.key) }}
            </code>
          </div>
        </div>

        <p v-if="message" :class="failed ? 'form-error' : 'settings-message'" :role="failed ? 'alert' : 'status'">{{ message }}</p>
        <div class="settings-actions">
          <small>Last saved {{ formatLastSaved(selectedTemplate.updatedAt) }}</small>
          <div>
            <button class="button button--quiet" type="button" :disabled="previewing || saving" @click="renderPreview">
              {{ previewing ? "Rendering…" : "Preview draft" }}
            </button>
            <button class="button button--primary" type="submit" :disabled="saving || previewing">
              {{ saving ? "Saving…" : "Save template" }}
            </button>
          </div>
        </div>

        <section v-if="preview" class="email-template-preview" aria-live="polite">
          <div><span>Rendered subject</span><strong>{{ preview.subject }}</strong></div>
          <div><span>Plain text</span><pre>{{ preview.textBody }}</pre></div>
          <div v-if="preview.htmlBody !== null"><span>HTML preview</span><iframe title="Rendered email HTML preview" sandbox="" :srcdoc="preview.htmlBody"></iframe></div>
        </section>
      </form>
    </div>
  </section>
</template>
